/**
 * @file Publishes the app-wide antd `message` and `Modal` imperative APIs plus the
 * single Firestore write-error handler on `MessageContext`, and renders both
 * context holders. Consumed by every surface that toasts or confirms, notably
 * `useFieldUI`, `useCellUI` and the settings forms.
 *
 * The context value's identity is load-bearing: `useFieldUI` lists
 * `handleFirestoreWriteError` in the deps of its debounced-save `useMemo`, and a
 * cleanup effect cancels the pending save whenever that debouncer's identity
 * changes. An unmemoized value therefore drops every field's in-flight 600ms
 * write on any provider render — and since the handler itself toasts, the loss is
 * self-triggering. Both the handler and the value must stay referentially stable;
 * `message.useMessage()` and `Modal.useModal()` already are.
 */
import { FC, PropsWithChildren, useCallback, useMemo } from 'react';
import { message, Modal } from 'antd';
import { MessageContext, FirestoreWriteErrorOptions, MessageContextType } from './MessageContext';
import { pendingSavesActions } from '../store/pendingSavesStore';

export const MessageProvider: FC<PropsWithChildren> = ({ children }) => {
  const [messageApi, messageContextHolder] = message.useMessage({
    duration: 12,
    maxCount: 3,
  });
  const [modal, modalContextHolder] = Modal.useModal();

  const handleFirestoreWriteError = useCallback((
    error: unknown,
    context: string,
    options: FirestoreWriteErrorOptions = {}
  ): Error => {
    const normalizedError = error instanceof Error ? error : new Error(String(error));
    const errorCode = (normalizedError as unknown as { code?: unknown }).code;

    console.error('[firestore] write operation failed', {
      context,
      error: normalizedError,
    });

    if (options.onError) {
      options.onError(normalizedError);
    }

    const messageText =
      options.userMessage ??
      (errorCode === 'permission-denied'
        ? "You don't have permission to do that."
        : 'Failed to save changes. Please try again.');
    messageApi.error(messageText);

    if (options.saveId) {
      pendingSavesActions.markFailed(options.saveId);
    }

    (normalizedError as any).__firestoreHandled = true;

    return normalizedError;
  }, [messageApi]);

  const value = useMemo<MessageContextType>(() => ({
    messageApi,
    modal,
    handleFirestoreWriteError,
  }), [messageApi, modal, handleFirestoreWriteError]);

  return (
    <MessageContext.Provider value={value}>
      {messageContextHolder}
      {modalContextHolder}
      {children}
    </MessageContext.Provider>
  );
};
